<div class="notification-list-container">

  <!-- Page Header -->
  <div class="page-header">
    <div class="page-header-text">
      <h1>Notification Management</h1>
      <p class="subtitle">Create and manage system-wide notifications delivered in real time.</p>
    </div>
    <button mat-raised-button color="primary" class="new-btn" *featureFlag="'NOTIFICATION_CREATE'" (click)="onNewNotification()">
      <mat-icon>add</mat-icon>
      New Notification
    </button>
  </div>

  <!-- Filter Section -->
  <div class="filter-section">

    <!-- Quick filter chips + toggle -->
    <div class="quick-filter-row">
      <div class="chip-group">
        <button class="chip-button" [class.active]="activeChip === 'all'" (click)="applyQuickFilter('all')">All Time</button>
        <button class="chip-button" [class.active]="activeChip === 'today'" (click)="applyQuickFilter('today')">Today</button>
        <button class="chip-button" [class.active]="activeChip === 'week'" (click)="applyQuickFilter('week')">This Week</button>
        <button class="chip-button" [class.active]="activeChip === 'month'" (click)="applyQuickFilter('month')">Last Month</button>
      </div>
      <button mat-stroked-button class="filters-toggle" [class.active]="showAdvancedFilters" (click)="toggleAdvancedFilters()">
        <mat-icon>tune</mat-icon>
        Filters
      </button>
    </div>

    <!-- Advanced filter bar (collapsible) -->
    <div class="advanced-filters" *ngIf="showAdvancedFilters" [@slideDown]>
      <div class="filter-row">

        <mat-form-field appearance="outline" class="date-field">
          <mat-label>From Date</mat-label>
          <input matInput [matDatepicker]="fromPicker" [value]="dateFrom" (dateChange)="onDateFromChange($event.value)" placeholder="MM/DD/YYYY" />
          <mat-datepicker-toggle matIconSuffix [for]="fromPicker"></mat-datepicker-toggle>
          <mat-datepicker #fromPicker></mat-datepicker>
        </mat-form-field>

        <mat-form-field appearance="outline" class="date-field">
          <mat-label>To Date</mat-label>
          <input matInput [matDatepicker]="toPicker" [value]="dateTo" (dateChange)="onDateToChange($event.value)" placeholder="MM/DD/YYYY" />
          <mat-datepicker-toggle matIconSuffix [for]="toPicker"></mat-datepicker-toggle>
          <mat-datepicker #toPicker></mat-datepicker>
        </mat-form-field>

        <mat-form-field appearance="outline" class="type-field">
          <mat-label>Type</mat-label>
          <mat-select [value]="selectedType" (selectionChange)="onTypeChange($event.value)">
            <mat-option value="">All</mat-option>
            <mat-option *ngFor="let opt of typeOptions" [value]="opt.value">{{ opt.label }}</mat-option>
          </mat-select>
        </mat-form-field>

        <mat-form-field appearance="outline" class="search-field">
          <mat-label>Search</mat-label>
          <input matInput [value]="searchKeyword" (input)="onSearch($any($event.target).value)" placeholder="Search by title or content…" />
          <mat-icon matSuffix>search</mat-icon>
        </mat-form-field>

        <button mat-icon-button (click)="clearAllFilters()" matTooltip="Clear filters" aria-label="Clear filters">
          <mat-icon>close</mat-icon>
        </button>

      </div>
    </div>

  </div>

  <!-- Loading State -->
  <div class="loading-container" *ngIf="isLoading">
    <mat-spinner diameter="40"></mat-spinner>
    <p>Loading notifications...</p>
  </div>

  <!-- Table -->
  <div class="table-card" *ngIf="!isLoading">

    <!-- Empty State: no data at all -->
    <div class="empty-state" *ngIf="dataSource.data.length === 0">
      <mat-icon class="empty-icon">notifications_none</mat-icon>
      <h3>No notifications yet</h3>
      <p>Create your first notification to reach all online users.</p>
      <button mat-raised-button color="primary" (click)="onNewNotification()">
        <mat-icon>add</mat-icon>
        New Notification
      </button>
    </div>

    <!-- Empty State: data exists but filter returns nothing -->
    <div class="empty-state" *ngIf="dataSource.data.length > 0 && dataSource.filteredData.length === 0">
      <mat-icon class="empty-icon">search_off</mat-icon>
      <h3>No notifications found</h3>
      <p>Try adjusting your filters or selecting a different time range.</p>
    </div>

    <!-- Data Table -->
    <table mat-table [dataSource]="dataSource" class="notification-table" *ngIf="dataSource.filteredData.length > 0">

      <!-- Title Column -->
      <ng-container matColumnDef="title">
        <th mat-header-cell *matHeaderCellDef>Title</th>
        <td mat-cell *matCellDef="let row">
          <div class="cell-title">{{ row.title }}</div>
          <div class="cell-preview">{{ stripHtml(row.content) }}</div>
        </td>
      </ng-container>

      <!-- Type Column -->
      <ng-container matColumnDef="type">
        <th mat-header-cell *matHeaderCellDef>Type</th>
        <td mat-cell *matCellDef="let row">
          <span class="type-badge" [ngClass]="'type-' + row.type">
            <mat-icon>{{ getTypeIcon(row.type) }}</mat-icon>
            {{ getTypeLabel(row.type) }}
          </span>
        </td>
      </ng-container>

      <!-- Publish Date Column -->
      <ng-container matColumnDef="publishDate">
        <th mat-header-cell *matHeaderCellDef>Publish Date</th>
        <td mat-cell *matCellDef="let row">{{ row.publishedOn | date:'MMM d, y' }}</td>
      </ng-container>

      <!-- Last Modified Column -->
      <ng-container matColumnDef="lastModified">
        <th mat-header-cell *matHeaderCellDef>Last Modified</th>
        <td mat-cell *matCellDef="let row">
          <span *ngIf="row.modifiedOn">{{ row.modifiedOn | date:'MMM d, y' }}</span>
          <span class="cell-none" *ngIf="!row.modifiedOn">—</span>
        </td>
      </ng-container>

      <!-- Actions Column -->
      <ng-container matColumnDef="actions">
        <th mat-header-cell *matHeaderCellDef></th>
        <td mat-cell *matCellDef="let row" class="actions-cell">
          <button mat-icon-button [matMenuTriggerFor]="rowMenu" (click)="$event.stopPropagation()">
            <mat-icon>more_vert</mat-icon>
          </button>
          <mat-menu #rowMenu="matMenu">
            <button mat-menu-item (click)="onEdit(row, $event)">
              <mat-icon color="primary">edit</mat-icon>
              Edit
            </button>
            <button mat-menu-item class="delete-item" (click)="onDelete(row, $event)">
              <mat-icon>delete_outline</mat-icon>
              Delete
            </button>
          </mat-menu>
        </td>
      </ng-container>

      <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
      <tr mat-row *matRowDef="let row; columns: displayedColumns;" class="clickable-row" (click)="onEdit(row, $event)"></tr>
    </table>

    <mat-paginator
      *ngIf="dataSource.filteredData.length > 0"
      [pageSize]="pageSize"
      [pageSizeOptions]="pageSizeOptions"
      showFirstLastButtons
      aria-label="Notification list pagination">
    </mat-paginator>

  </div>
</div>
